/**
 * Templates Page
 * Create and edit the design templates, and name their fields. A template
 * generates designs once its ID is added to autotemplated_templates.json and
 * its switch is on.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import ImageIcon from '@mui/icons-material/Image';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import AppLayout from '../components/Layout/AppLayout';
import ConfirmDialog from '../components/Common/ConfirmDialog';
import api from '../services/api';
import TemplatePreview from '../at-editor/TemplatePreview.jsx';
import '../at-editor/editor.css';

const PLATFORMS = {
  instagram: { label: 'Instagram', color: '#E4405F', Icon: InstagramIcon },
  linkedin: { label: 'LinkedIn', color: '#0A66C2', Icon: LinkedInIcon },
  facebook: { label: 'Facebook', color: '#1877F2', Icon: FacebookIcon },
  external_flyer: { label: 'External Flyer', color: '#666666', Icon: ImageIcon },
};

const fieldLabel = (name = '') => name.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const fieldLayers = (template) => (template.layers || []).filter((l) => l.data_field);

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState(null);
  const [error, setError] = useState('');
  const [newDialog, setNewDialog] = useState(null); // { platform, copyFrom? }
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      setTemplates((await api.get('/editor/templates')).data);
    } catch {
      setError('Could not load templates.');
    }
  }, []);

  // Thumbnails refresh shortly after editing stops, so look again a little
  // later, and whenever this tab comes back into view.
  useEffect(() => {
    load();
    const later = setTimeout(load, 25000);
    window.addEventListener('focus', load);
    return () => {
      clearTimeout(later);
      window.removeEventListener('focus', load);
    };
  }, [load]);

  const setActive = async (template, active) => {
    const { data } = await api.put(`/editor/templates/${template.id}/active`, { active });
    setTemplates((list) => list.map((t) => (t.id === data.id ? data : t)));
  };

  // One switch per platform: turns all of its switchable templates on or off
  const setPlatformActive = async (platform, active) => {
    try {
      const { data } = await api.post('/editor/templates/platform-active', { platform, active });
      const byId = Object.fromEntries(data.map((t) => [t.id, t]));
      setTemplates((list) => list.map((t) => byId[t.id] || t));
    } catch {
      setError('Could not switch the templates. Please try again.');
    }
  };

  const remove = async () => {
    await api.delete(`/editor/templates/${deleting.id}`);
    setTemplates((list) => list.filter((t) => t.id !== deleting.id));
    setDeleting(null);
  };

  return (
    <AppLayout>
      <Box>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" gutterBottom fontWeight={600}>
              Templates
            </Typography>
            <Typography variant="body1" color="text.secondary">
              The layouts for job post designs. A template creates a design for each job once it is connected to generation and switched on.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setNewDialog({ platform: 'instagram' })}
            sx={{ bgcolor: '#D32F2F', '&:hover': { bgcolor: '#B71C1C' }, boxShadow: 'none', flexShrink: 0 }}
          >
            New template
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {!templates && !error && <CircularProgress />}

        {templates && Object.entries(PLATFORMS).map(([platform, meta]) => {
          const items = templates
            .filter((t) => t.platform === platform)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
          // Templates whose own switch works (connected, not disabled in the config)
          const switchable = items.filter((t) => GENERATION_STATUS[t.generation_status]?.switchable);
          const onCount = switchable.filter((t) => t.active).length;
          return (
            <Box key={platform} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <meta.Icon sx={{ color: meta.color }} />
                <Typography variant="h6">{meta.label}</Typography>
                <Chip size="small" label={items.length} />
                <Box sx={{ flex: 1 }} />
                <Tooltip title={switchable.length
                  ? `Switch all ${switchable.length} connected ${meta.label} templates on or off`
                  : `No ${meta.label} templates are connected to generation yet`}
                >
                  <FormControlLabel
                    labelPlacement="start"
                    control={
                      <Switch
                        checked={switchable.length > 0 && onCount === switchable.length}
                        disabled={!switchable.length}
                        onChange={(e) => setPlatformActive(platform, e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary">
                        {switchable.length ? `${onCount} of ${switchable.length} on` : 'None connected'}
                      </Typography>
                    }
                    sx={{ mr: 0.5 }}
                  />
                </Tooltip>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  alignItems: 'stretch',
                  overflowX: 'auto',
                  px: 0.5,
                  pt: 0.5,
                  pb: 2,
                  '&::-webkit-scrollbar': { height: 6 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 3 },
                }}
              >
                {items.map((t) => (
                  <Box key={t.id} sx={{ flex: `0 0 ${CARD_WIDTH}px`, minWidth: 0 }}>
                    <TemplateCard
                      template={t}
                      onEdit={() => navigate(`/editor/template/${t.id}`)}
                      onDuplicate={() => setNewDialog({ platform, copyFrom: t })}
                      onDelete={() => setDeleting(t)}
                      onActive={(active) => setActive(t, active)}
                    />
                  </Box>
                ))}
                {!items.length && (
                  <Box sx={{ flex: `0 0 ${CARD_WIDTH}px`, minWidth: 0 }}>
                    <Card
                      variant="outlined"
                      onClick={() => setNewDialog({ platform })}
                      sx={{
                        height: '100%',
                        minHeight: 220,
                        borderStyle: 'dashed',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        color: 'text.secondary',
                        p: 3,
                        bgcolor: 'transparent',
                        cursor: 'pointer',
                        '&:hover': { borderColor: '#D32F2F', color: '#D32F2F' },
                      }}
                    >
                      <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="body2">Add a {meta.label} template</Typography>
                    </Card>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {newDialog && (
        <NewTemplateDialog
          initial={newDialog}
          onClose={() => setNewDialog(null)}
          onCreated={(t) => navigate(`/editor/template/${t.id}`)}
        />
      )}
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete template?"
        message={`"${deleting?.name}" will be removed. Designs already generated from it stay in History.`}
        onConfirm={remove}
        onClose={() => setDeleting(null)}
        onCancel={() => setDeleting(null)}
      />
    </AppLayout>
  );
}

const CARD_WIDTH = 280;

const GENERATION_STATUS = {
  on: { label: 'Generating designs', color: 'success', switchable: true, hint: 'New jobs create a design from this template.' },
  off: { label: 'Switched off', color: 'default', switchable: true, hint: 'Connected, but paused by the switch below.' },
  disabled: { label: 'Disabled in config', color: 'default', switchable: false, hint: 'Marked "disabled": true in autotemplated_templates.json.' },
  not_connected: { label: 'Not connected to generation', color: 'warning', switchable: false, hint: 'Add this template ID to autotemplated_templates.json.' },
};

function TemplateCard({ template, onEdit, onDuplicate, onDelete, onActive }) {
  const fields = fieldLayers(template);
  const status = GENERATION_STATUS[template.generation_status] || GENERATION_STATUS.not_connected;
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: template.generation_status === 'on' ? 1 : 0.65, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 6 } }}>
      <Box onClick={onEdit} sx={{ cursor: 'pointer', bgcolor: 'grey.100', display: 'flex', justifyContent: 'center', borderBottom: 1, borderColor: 'divider' }}>
        {template.thumbnail_url ? (
          <img
            src={template.thumbnail_url}
            alt={template.name}
            style={{ width: '100%', aspectRatio: `${template.width} / ${template.height}`, display: 'block' }}
          />
        ) : (
          /* Not rendered yet (a new template): AutoTemplated's live preview */
          <Box className="at-editor" data-theme="light" sx={{ background: 'transparent !important', lineHeight: 0 }}>
            <TemplatePreview template={template} width={240} height={Math.round((240 * template.height) / template.width)} />
          </Box>
        )}
      </Box>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle1" fontWeight={600} noWrap title={template.name}>
          {template.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
          {template.width} × {template.height} px · {fields.length} data fields
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Tooltip title="Add this ID to autotemplated_templates.json to connect the template to generation">
            <Typography variant="caption" noWrap sx={{ fontFamily: 'monospace', fontSize: 10.5, userSelect: 'all', minWidth: 0 }}>
              {template.id}
            </Typography>
          </Tooltip>
          <Tooltip title="Copy template ID">
            <IconButton size="small" onClick={() => navigator.clipboard?.writeText(template.id)}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Tooltip title={status.hint}>
          <Chip size="small" color={status.color} variant="outlined" label={status.label} sx={{ alignSelf: 'flex-start', mb: 1.5, fontSize: 11 }} />
        </Tooltip>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
          {fields.map((l) => (
            <Chip key={l.id} size="small" variant="outlined" label={fieldLabel(l.name)} sx={{ fontSize: 11 }} />
          ))}
        </Box>
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={<Switch size="small" checked={template.active} disabled={!status.switchable} onChange={(e) => onActive(e.target.checked)} />}
          label={<Typography variant="body2">Used for new designs</Typography>}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={onEdit}
            sx={{ flex: 1, bgcolor: '#D32F2F', '&:hover': { bgcolor: '#B71C1C' }, boxShadow: 'none' }}
          >
            Edit
          </Button>
          <Tooltip title="Duplicate">
            <IconButton onClick={onDuplicate}><ContentCopyIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={onDelete}><DeleteOutlineIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
}

function NewTemplateDialog({ initial, onClose, onCreated }) {
  const copyFrom = initial.copyFrom;
  const [name, setName] = useState(copyFrom ? `${copyFrom.name} copy` : '');
  const [platform, setPlatform] = useState(initial.platform);
  const [width, setWidth] = useState(1080);
  const [height, setHeight] = useState(1080);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('/editor/templates', {
        name: name.trim(),
        platform,
        width: Number(width),
        height: Number(height),
        copy_from: copyFrom?.id || null,
      });
      onCreated(data);
    } catch (err) {
      setError(err.response?.data?.detail?.toString() || 'Could not create the template.');
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{copyFrom ? 'Duplicate template' : 'New template'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
        <TextField select label="Platform" value={platform} onChange={(e) => setPlatform(e.target.value)}>
          {Object.entries(PLATFORMS).map(([key, meta]) => (
            <MenuItem key={key} value={key}>{meta.label}</MenuItem>
          ))}
        </TextField>
        {!copyFrom && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Width (px)" type="number" value={width} onChange={(e) => setWidth(e.target.value)} />
            <TextField label="Height (px)" type="number" value={height} onChange={(e) => setHeight(e.target.value)} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!name.trim() || saving}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}
